import React from 'react';
import { Save, Upload, CheckCircle, History, Sparkles, Lock, FlaskConical } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { cn } from '@/lib/utils';

interface EditorToolbarProps {
  onSave: () => void;
  onPublish: () => void;
  onValidate: () => void;
  onToggleVersions: () => void;
  onToggleAssistant?: () => void;
  onRunInIDE?: () => void;
  hasUnsavedChanges: boolean;
  isPublished: boolean;
  disabled: boolean;
  assistantOpen?: boolean;
  readOnly?: boolean;
  contentType?: string;
}

export function EditorToolbar({
  onSave,
  onPublish,
  onValidate,
  onToggleVersions,
  onToggleAssistant,
  onRunInIDE,
  hasUnsavedChanges,
  isPublished,
  disabled,
  assistantOpen,
  readOnly = false,
  contentType,
}: EditorToolbarProps) {
  return (
    <div className="flex items-center gap-2 px-4 py-2 border-b bg-background">
      {/* Save/Publish Actions or Read-Only Badge */}
      {readOnly ? (
        <Badge variant="secondary" className="flex items-center gap-1">
          <Lock className="h-3 w-3" />
          Read-Only
        </Badge>
      ) : (
        <div className="flex gap-1">
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant={hasUnsavedChanges ? 'default' : 'secondary'}
                onClick={onSave}
                disabled={disabled || !hasUnsavedChanges}
              >
                <Save className="h-4 w-4 mr-2" />
                Save Draft
              </Button>
            </TooltipTrigger>
            <TooltipContent>Save as Draft (Ctrl+S)</TooltipContent>
          </Tooltip>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button
                size="sm"
                variant="default"
                className="bg-green-600 hover:bg-green-700"
                onClick={onPublish}
                disabled={disabled}
              >
                <Upload className="h-4 w-4 mr-2" />
                Publish
              </Button>
            </TooltipTrigger>
            <TooltipContent>Publish (Ctrl+Shift+S)</TooltipContent>
          </Tooltip>
        </div>
      )}

      <Separator orientation="vertical" className="h-6" />

      {/* Validation */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button size="sm" variant="outline" onClick={onValidate} disabled={disabled}>
            <CheckCircle className="h-4 w-4 mr-2" />
            Validate
          </Button>
        </TooltipTrigger>
        <TooltipContent>Validate content</TooltipContent>
      </Tooltip>

      {/* Run in IDE - only for agents */}
      {contentType === 'agent' && onRunInIDE && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button size="sm" variant="outline" onClick={onRunInIDE} disabled={disabled}>
              <FlaskConical className="h-4 w-4 mr-2" />
              Run in IDE
            </Button>
          </TooltipTrigger>
          <TooltipContent>Test agent in IDE</TooltipContent>
        </Tooltip>
      )}

      <div className="flex-1" />

      {/* Version History Toggle */}
      <Tooltip>
        <TooltipTrigger asChild>
          <Button size="sm" variant="ghost" onClick={onToggleVersions} disabled={disabled}>
            <History className="h-4 w-4" />
          </Button>
        </TooltipTrigger>
        <TooltipContent>Version History</TooltipContent>
      </Tooltip>

      {/* AI Assistant Toggle */}
      {onToggleAssistant && (
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant={assistantOpen ? 'secondary' : 'ghost'}
              onClick={onToggleAssistant}
              disabled={disabled}
              className={cn(
                assistantOpen && 'bg-purple-100 text-purple-700 hover:bg-purple-200 dark:bg-purple-900 dark:text-purple-300'
              )}
            >
              <Sparkles className="h-4 w-4" />
            </Button>
          </TooltipTrigger>
          <TooltipContent>AI Assistant</TooltipContent>
        </Tooltip>
      )}
    </div>
  );
}

export default EditorToolbar;
